// The effects' sprite sheet, drawn once at start-up with Canvas 2D: 4 × 4 cells of 128 px, white shapes on
// transparent (the particle colour tints them). Seeded, so every visit draws the same smoke.

export const CELL = 128;
export const GRID = 4;

export const S_GLOW = 0, S_FLASH = 1, S_SMOKE = 2, S_SMOKE2 = 3, S_SMOKE3 = 4, S_STREAK = 5, S_RING = 6, S_SCORCH = 7,
  S_FLAME = 8, S_EMBER = 9, S_SHOCK = 10, S_DEBRIS = 11, S_DUST = 12, S_SPLASH = 13, S_STAR = 14, S_ASH = 15;

function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Ctx = CanvasRenderingContext2D;

function softDot(g: Ctx, x: number, y: number, r: number, a: number, lum = 255) {
  const grad = g.createRadialGradient(x, y, 0, x, y, r);
  grad.addColorStop(0, `rgba(${lum},${lum},${lum},${a})`);
  grad.addColorStop(0.55, `rgba(${lum},${lum},${lum},${a * 0.45})`);
  grad.addColorStop(1, `rgba(${lum},${lum},${lum},0)`);
  g.fillStyle = grad;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
}

function glow(g: Ctx) {
  const c = CELL / 2;
  const grad = g.createRadialGradient(c, c, 0, c, c, c);
  for (const [s, a] of [[0, 1], [0.12, 0.85], [0.3, 0.45], [0.55, 0.16], [0.8, 0.04], [1, 0]]) grad.addColorStop(s, `rgba(255,255,255,${a})`);
  g.fillStyle = grad;
  g.fillRect(0, 0, CELL, CELL);
}

function flash(g: Ctx) {
  const c = CELL / 2;
  glow(g);
  g.globalCompositeOperation = "lighter";
  for (let k = 0; k < 6; k++) {
    g.save();
    g.translate(c, c);
    g.rotate((k / 6) * Math.PI + 0.3);
    g.scale(1, 0.06);
    softDot(g, 0, 0, c * 0.95, 0.55);
    g.restore();
  }
  softDot(g, c, c, c * 0.28, 1);
  g.globalCompositeOperation = "source-over";
}

/** A billowing puff: many soft circles, lit from above. */
function smoke(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  for (let k = 0; k < 46; k++) {
    const ang = r() * Math.PI * 2, dist = Math.pow(r(), 0.7) * c * 0.5;
    const x = c + Math.cos(ang) * dist, y = c + Math.sin(ang) * dist * 0.9;
    const rad = c * (0.16 + r() * 0.3);
    const lum = Math.round(150 + 105 * (1 - (y - (c - c * 0.5)) / c)); // lighter on top
    softDot(g, x, y, rad, 0.1 + r() * 0.16, Math.min(255, Math.max(110, lum)));
  }
  // keep the edge soft: fade everything towards the cell border
  g.globalCompositeOperation = "destination-in";
  const grad = g.createRadialGradient(c, c, c * 0.25, c, c, c);
  grad.addColorStop(0, "rgba(0,0,0,1)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, CELL, CELL);
  g.globalCompositeOperation = "source-over";
}

function streak(g: Ctx) {
  const c = CELL / 2;
  g.save();
  g.translate(c, c);
  g.scale(1, 0.16);
  softDot(g, 0, 0, c * 0.98, 1);
  g.restore();
  g.save();
  g.translate(c, c);
  g.scale(1, 0.07);
  softDot(g, c * 0.25, 0, c * 0.6, 1);
  g.restore();
}

function ring(g: Ctx, width: number, soft: number) {
  const c = CELL / 2, R = c * 0.82;
  const grad = g.createRadialGradient(c, c, Math.max(0, R - width - soft), c, c, R + soft);
  grad.addColorStop(0, "rgba(255,255,255,0)");
  grad.addColorStop((width * 0.2) / (width + 2 * soft), "rgba(255,255,255,0.15)");
  grad.addColorStop((width + soft) / (width + 2 * soft), "rgba(255,255,255,1)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.beginPath();
  g.arc(c, c, R + soft, 0, Math.PI * 2);
  g.fill();
}

function scorch(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  softDot(g, c, c, c * 0.62, 0.7);
  for (let k = 0; k < 34; k++) {
    const ang = r() * Math.PI * 2, dist = Math.pow(r(), 0.6) * c * 0.62;
    softDot(g, c + Math.cos(ang) * dist, c + Math.sin(ang) * dist, c * (0.06 + r() * 0.2), 0.35 + r() * 0.4);
  }
  // radial streaks of blast debris
  g.strokeStyle = "rgba(255,255,255,0.35)";
  g.lineCap = "round";
  for (let k = 0; k < 18; k++) {
    const ang = r() * Math.PI * 2, len = c * (0.55 + r() * 0.4);
    g.lineWidth = 1 + r() * 2.5;
    g.beginPath();
    g.moveTo(c + Math.cos(ang) * c * 0.3, c + Math.sin(ang) * c * 0.3);
    g.lineTo(c + Math.cos(ang) * len, c + Math.sin(ang) * len);
    g.stroke();
  }
}

function flame(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  // a tongue of fire pointing up: stacked soft dots, narrowing and fading towards the tip
  for (let k = 0; k < 28; k++) {
    const f = k / 28;
    const y = CELL * 0.86 - f * CELL * 0.74;
    const x = c + Math.sin(f * 7 + r() * 0.8) * c * 0.1 * f;
    softDot(g, x, y, c * (0.42 - f * 0.32), 0.2 + (1 - f) * 0.2, 255);
  }
  softDot(g, c, CELL * 0.78, c * 0.3, 0.6, 255);
}

function ember(g: Ctx) {
  const c = CELL / 2;
  softDot(g, c, c, c * 0.5, 0.5);
  softDot(g, c, c, c * 0.16, 1);
}

function debris(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  g.fillStyle = "rgba(255,255,255,1)";
  g.beginPath();
  for (let k = 0; k < 7; k++) {
    const ang = (k / 7) * Math.PI * 2, rad = c * (0.25 + r() * 0.35);
    const x = c + Math.cos(ang) * rad, y = c + Math.sin(ang) * rad;
    if (k === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
}

function dust(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  for (let k = 0; k < 40; k++) {
    const x = c + (r() - 0.5) * c * 1.2, y = c + (r() - 0.5) * c * 0.55;
    softDot(g, x, y, c * (0.14 + r() * 0.26), 0.1 + r() * 0.12, 200 + Math.round(r() * 55));
  }
  g.globalCompositeOperation = "destination-in";
  g.save();
  g.translate(c, c);
  g.scale(1, 0.62);
  const grad = g.createRadialGradient(0, 0, c * 0.2, 0, 0, c);
  grad.addColorStop(0, "rgba(0,0,0,1)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = grad;
  g.fillRect(-c, -c / 0.62, CELL, CELL / 0.62);
  g.restore();
  g.globalCompositeOperation = "source-over";
}

function splash(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  // a column of spray, widest at the base
  for (let k = 0; k < 40; k++) {
    const f = r();
    const y = CELL * 0.9 - f * CELL * 0.8;
    const x = c + (r() - 0.5) * c * (0.25 + (1 - f) * 0.6);
    softDot(g, x, y, c * (0.05 + r() * 0.12) * (1.2 - f * 0.6), 0.25 + r() * 0.35);
  }
}

function star(g: Ctx) {
  const c = CELL / 2;
  for (const rot of [0, Math.PI / 2]) {
    g.save();
    g.translate(c, c);
    g.rotate(rot);
    g.scale(1, 0.05);
    softDot(g, 0, 0, c * 0.95, 0.9);
    g.restore();
  }
  softDot(g, c, c, c * 0.22, 1);
}

function ash(g: Ctx, seed: number) {
  const r = rng(seed), c = CELL / 2;
  g.fillStyle = "rgba(255,255,255,0.9)";
  g.beginPath();
  for (let k = 0; k < 5; k++) {
    const ang = (k / 5) * Math.PI * 2 + r() * 0.5, rad = c * (0.2 + r() * 0.25);
    const x = c + Math.cos(ang) * rad, y = c + Math.sin(ang) * rad * 0.6;
    if (k === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
}

/** Draw the sheet (512 × 512). */
export function drawSprites(): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = cv.height = CELL * GRID;
  const g = cv.getContext("2d")!;
  const cells: ((g: Ctx) => void)[] = [];
  cells[S_GLOW] = glow;
  cells[S_FLASH] = flash;
  cells[S_SMOKE] = (x) => smoke(x, 11);
  cells[S_SMOKE2] = (x) => smoke(x, 29);
  cells[S_SMOKE3] = (x) => smoke(x, 53);
  cells[S_STREAK] = streak;
  cells[S_RING] = (x) => ring(x, 3, 3);
  cells[S_SCORCH] = (x) => scorch(x, 7);
  cells[S_FLAME] = (x) => flame(x, 5);
  cells[S_EMBER] = ember;
  cells[S_SHOCK] = (x) => ring(x, 12, 9);
  cells[S_DEBRIS] = (x) => debris(x, 3);
  cells[S_DUST] = (x) => dust(x, 17);
  cells[S_SPLASH] = (x) => splash(x, 23);
  cells[S_STAR] = star;
  cells[S_ASH] = (x) => ash(x, 31);
  cells.forEach((draw, k) => {
    g.save();
    g.translate((k % GRID) * CELL, Math.floor(k / GRID) * CELL);
    g.beginPath();
    g.rect(0, 0, CELL, CELL);
    g.clip();
    draw(g);
    g.restore();
  });
  return cv;
}
